
import {ElPagination} from 'element-plus';
import noUiSlider, {target, Options} from 'nouislider';
import {defineComponent, inject, onMounted, ref, Ref, watch} from 'vue';
import VueScrollTo from 'vue-scrollto';
import {
    AjaxList,
    AjaxListFilterData,
    AjaxListItem,
    AjaxListProps,
    makeAjaxListProps
} from "@digitalwerk/frontend-muster/vue/composables/ajaxList";
import { setHistoryState, IHistoryStateProps, makeHistoryStateProps} from "@digitalwerk/frontend-muster/vue/composables/historyState";

interface ProductsListProps extends AjaxListProps, IHistoryStateProps {
    categories: { [key: string]: Array<number> };
}

interface ProductsListCategory {
    uid: number
}

interface ProductsListFilterData extends AjaxListFilterData {
    categories: { [key: string]: Array<number> };
    hoseProperties: {
        [key: string]: {
            min: number,
            max: number
        }
    };
}

class ProductsListItem extends AjaxListItem {
    categories: { [key: string]: Array<ProductsListCategory> } = {};
    tableProperties: {
        [key: number]: {
            maxWorkingPressure: number,
            radiusBend: number
        }
    } = {};
}

class ProductsList extends AjaxList<ProductsListItem, ProductsListFilterData> {
    filterItems() {
        const SLIDER_PROPERTIES_THRESHOLD_PERCENTAGE = {
            MIN: 10,
            MAX: 30
        };

        this.filteredItems.value = this.search.filterItems(
            this.items.value.filter((product) => {
                for (const category in this.filter.data.categories) {
                    if (!this.filter.data.categories.hasOwnProperty(category)) {
                        continue;
                    }

                    let filterCategory = this.filter.data.categories[category];

                    if (filterCategory.length) {
                        let isInCategory = false;
                        let productCategory = product.categories[category];

                        if (productCategory && productCategory.length) {
                            for (let productCategoryItem of productCategory) {
                                if (productCategoryItem.uid) {
                                    for (let filterCategoryItem of filterCategory) {
                                        if (productCategoryItem.uid === filterCategoryItem) {
                                            isInCategory = true;
                                            break;
                                        }
                                    }
                                    if (isInCategory) break;
                                }
                            }
                        }

                        if (!isInCategory) return false;
                    }
                }

                if (product.tableProperties) {
                    if (this.filter.data.hoseProperties.nominal) {
                        let nominalRange = this.filter.data.hoseProperties.nominal;
                        let isNominalInRange = false;
                        for (let tablePropertiesKey in product.tableProperties) {
                            let nominalValue = Number(tablePropertiesKey);
                            if (
                                (nominalValue >= nominalRange.min * (1 - SLIDER_PROPERTIES_THRESHOLD_PERCENTAGE.MIN / 100)) &&
                                (nominalValue <= nominalRange.max * (1 + SLIDER_PROPERTIES_THRESHOLD_PERCENTAGE.MAX / 100))
                            ) {
                                isNominalInRange = true;
                                break;
                            }
                        }
                        if (!isNominalInRange) return false;
                    }

                    if (this.filter.data.hoseProperties.pressure) {
                        let pressureRange = this.filter.data.hoseProperties.pressure;
                        let isPressureInRange = false;
                        for (let tablePropertiesKey in product.tableProperties) {
                            let maxWorkingPressure = product.tableProperties[tablePropertiesKey].maxWorkingPressure;
                            if (
                                (maxWorkingPressure >= pressureRange.min * (1 - SLIDER_PROPERTIES_THRESHOLD_PERCENTAGE.MIN / 100)) &&
                                (maxWorkingPressure <= pressureRange.max * (1 + SLIDER_PROPERTIES_THRESHOLD_PERCENTAGE.MAX / 100))
                            ) {
                                isPressureInRange = true;
                                break;
                            }
                        }
                        if (!isPressureInRange) return false;
                    }

                    if (this.filter.data.hoseProperties.bend) {
                        let bendRange = this.filter.data.hoseProperties.bend;
                        let isBendInRange = false;
                        for (let tablePropertiesKey in product.tableProperties) {
                            let radiusBend = product.tableProperties[tablePropertiesKey].radiusBend;
                            if (
                                (radiusBend >= bendRange.min * (1 - SLIDER_PROPERTIES_THRESHOLD_PERCENTAGE.MIN / 100)) &&
                                (radiusBend <= bendRange.max * (1 + SLIDER_PROPERTIES_THRESHOLD_PERCENTAGE.MAX / 100))
                            ) {
                                isBendInRange = true;
                                break;
                            }
                        }
                        if (!isBendInRange) return false;
                    }
                }

                return true;
            })
        );
    }
    setHistoryState() {
        let state:AjaxListFilterData = {
            ...this.filter.data,
            hoseProperties: {}
        }

        for (const [property] of Object.entries(this.filter.data.hoseProperties)) {
            if (this.filter.data.hoseProperties[property].min !== this.filter.initialData.hoseProperties[property].min) {
                state.hoseProperties[property] = {
                    min: this.filter.data.hoseProperties[property].min
                }
            }
            if (this.filter.data.hoseProperties[property].max !== this.filter.initialData.hoseProperties[property].max) {
                if (!state.hoseProperties[property]) {
                    state.hoseProperties[property] = {}
                }

                state.hoseProperties[property].max = this.filter.data.hoseProperties[property].max;
            }
        }

        if (this.props.id) {
            setHistoryState(this.props.id, state, this.props.urlEncoding);
        }
    }
}

function initRangeSlider(list: ProductsList, property: string): void {
    const slider: target | null = document.getElementById(`product-filter-slider-${property}`);

    if (!slider) {
        return;
    }

    const range = {
        min: Number(slider.dataset.min),
        max: Number(slider.dataset.max)
    };

    list.filter.initialData.hoseProperties[property] = range;
    list.filter.data.hoseProperties[property] = range;

    const sliderOptions: Options = {
        start: [range.min, range.max],
        connect: [false, true, false],
        range,
        step: 1,
        tooltips: [true, true],
        format: {
            to: (value: number) => {
                return Number(value).toFixed(0);
            },
            from: (value: string) => {
                return Number(value);
            }
        }
    };

    noUiSlider
        .create(slider, sliderOptions)
        .on('change', (values) => {
            list.filter.data.hoseProperties[property] = {
                min: Number(values[0]),
                max: Number(values[1])
            }
        });

    watch(() => list.filter.data.hoseProperties[property], (values) => {
        if (slider.noUiSlider) {
            slider.noUiSlider.set([values.min, values.max]);
        }
    }, {deep: true});
}

export default defineComponent({
    name: 'products-list',
    components: {
        ElPagination
    },
    props: {
        ...makeAjaxListProps(),
        ...makeHistoryStateProps(),
        categories: {
            type: Object
        },
        showTeaser: Boolean,
    },
    setup(props, context) {
        const list = new ProductsList(props as ProductsListProps, {
            categories: props.categories as { [key: string]: Array<number> },
            hoseProperties: {}
        });
        const listElement: Ref<Element | null> = ref(null);
        const scrollOffset = inject('scrollOffset') || 0;
        const view = 'thumbs';

        const itemsPerPage = ref(props.itemsPerPage);
        const showTeaser = ref(props.showTeaser);
        const toggleTextBtn = ref(false);


        const handlePaginationClick = (page: number) => {
            list.pagination.currentPage.value = page;

            if (listElement.value) {
                VueScrollTo.scrollTo(listElement.value, 0, {
                    offset: scrollOffset
                });
            }
        };
        onMounted(() => {
            initRangeSlider(list, 'nominal');
            initRangeSlider(list, 'pressure');
            initRangeSlider(list, 'bend');
        })

        return {
            handlePaginationClick,
            itemsPerPage,
            list,
            listElement,
            showTeaser,
            view,
            toggleTextBtn,
        }
    }
});
